// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsSims from '../../../../api/actionsSims';
// Components
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import AddSimCardsByProvider from '../../addSimCard/components/AddSimCardsByProvider';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import splitLines from '../../../../funcs/splitLines';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 *
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @returns {JSX} CustomPayModalAssignSimCards
 */
function CustomPayModalAssignSimCards({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.assign.steps';

    // State
    const [step, setStep] = useState(0);
    const [clientSelected, setClientSelected] = useState(getInitialValueForRowSelected());
    const [simNumberList, setSimNumberList] = useState('');
    const [disableSubmit, setDisableSubmit] = useState(false);

    // API Calls Assign SimCard
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity={'modal.assign.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.assign.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitAssignSimCards = actionsSims.assignSimCards(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.client_selection`,
                disabledNext: !Object.keys(clientSelected.rowIds).length
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.assign_sims`,
                disabledNext: false
            }
        }),
        [clientSelected]
    );

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step !== 0 ? 'max-w-lg' : 'max-w-6xl'}>
                <HeaderTitle title={'modal.assign.title'} namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <OrganizationsTable
                            filtersEventName={setFiltersEventName('organizations', true)}
                            singleSelection={true}
                            rowsSelected={clientSelected}
                            setRowsSelected={setClientSelected}
                        />
                    }
                    {
                        step === 1 &&
                        <AddSimCardsByProvider
                            simNumberList={simNumberList}
                            setSimNumberList={setSimNumberList}
                            setDisableSubmit={setDisableSubmit}
                        />
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    stepsNumber={STEPS_NUMBER}
                    setStep={setStep}
                    onValidate={() => {
                        submitAssignSimCards.mutate({
                            iccids: splitLines(simNumberList),
                            organizationId: clientSelected.rowsData[0].uuid
                        });
                    }}
                    disableValidation={disableSubmit || disabledNext}
                    isLoading={submitAssignSimCards.isLoading}
                />
            </ModalContent>
        </Modal>
    );
}

CustomPayModalAssignSimCards.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default CustomPayModalAssignSimCards;